import { css, cx } from "@emotion/css";
import { CloudArrowUpIcon, TrashIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, borderWidth, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type React from "react";
import { useId, useMemo, useState } from "react";
import type { FileRejection } from "react-dropzone";
import { ErrorCode, useDropzone } from "react-dropzone";
import { resetStyles } from "../../../utils";
import { Button } from "../../Button";
import type { PopoverBasicHelpProps } from "../../Popover";
import { FormDescription } from "../Primitives/FormDescription";
import { InputError } from "../Primitives/InputError";
import InputLabel from "../Primitives/InputLabel";
import type { DescriptionContent } from "../utils/descriptionWithLinks";
export interface FileUploadProps {
    /**
     * The label for the file upload.
     */
    label: string;
    /**
     * Optional description paragraph to display under the label.
     * Can be either:
     * - A simple string (basic usage)
     * - A DescriptionContent array created with descriptionText`...` for rich content with links and code
     * @example
     * // Simple string
     * description="This is a basic description."
     *
     * // Rich content with links and code
     * description={descriptionText`Set ${descriptionCode("isVisible")} prop or see ${descriptionLink("docs", "/docs")}.`}
     */
    description?: string | DescriptionContent;
    /**
     * PopoverBasicHelp component to display additional help information
     * displays the Information icon next to the label
     */
    popover?: React.ReactElement<PopoverBasicHelpProps>;
    /**
     * If true displays the (optional) text next to the label
     */
    hasOptionalMarker?: boolean;
    /**
     * If true displays the (required) text next to the label
     */
    hasRequiredMarker?: boolean;
    /**
     * Whether this field has a default value marker
     */
    hasDefaultMarker?: boolean;
    /**
     * Error message to display.
     */
    error?: string;
    /**
     * The action to perform when the form control field is changed.
     */
    onChange: (newValue: File[]) => void;
    /**
     * The max number of files that can be selected.
     * @default 1
     */
    filesLimit?: number;
    /**
     * The max file size in bytes.
     */
    maxFileSizeBytes?: number;
    /**
     * The supported file types.
     * These must be case insensitive filename extensions starting with a period e.g. ".txt", ".jpg"
     */
    acceptedFileTypes?: string[];
}
const ONE_TB_BYTES = 1000 * 1000 * 1000 * 1000;
const MAX_SUPPORTED_FILE_SIZE = ONE_TB_BYTES;
export function FileUpload({ label, description, popover, hasOptionalMarker, hasRequiredMarker, hasDefaultMarker, error, onChange, filesLimit = 1, maxFileSizeBytes = MAX_SUPPORTED_FILE_SIZE, acceptedFileTypes }: FileUploadProps) {
    const baseId = useId();
    const inputId = `fileUpload-${baseId}`;
    if (maxFileSizeBytes <= 0) {
        maxFileSizeBytes = MAX_SUPPORTED_FILE_SIZE;
    }
    const acceptedFileTypesSet = useMemo(() => new Set(acceptedFileTypes?.map((ext) => ext.toLowerCase().trim())), [acceptedFileTypes]);
    const [files, setFiles] = useState<File[]>([]);
    const [internalError, setInternalError] = useState<string | undefined>();
    const maxFileSizeText = formatBytes(maxFileSizeBytes);
    const handleChange = (files: File[]) => {
        setInternalError(undefined);
        setFiles(files);
        onChange(files);
    };
    const handleDrop = (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
        if (filesLimit > 1 && files.length + acceptedFiles.length > filesLimit) {
            setInternalError(`Maximum number of files exceeded. You can upload up to ${filesLimit} files.`);
            return;
        }
        if (acceptedFiles.length > 0) {
            handleChange(filesLimit <= 1 ? acceptedFiles : [...files, ...acceptedFiles]);
        }
        if (rejectedFiles.length === 0) {
            setInternalError(undefined);
        }
        else {
            if (rejectedFiles.length > 0 && rejectedFiles[0].errors.length > 0) {
                const rejectedFile = rejectedFiles[0];
                const error = rejectedFiles[0].errors[0];
                let message = "";
                switch (error.code) {
                    case ErrorCode.FileTooLarge:
                        message = `File ${rejectedFile.file.name} is too big. Size limit is ` + maxFileSizeText + ".";
                        break;
                    case ErrorCode.FileInvalidType:
                        message = `File type for ${rejectedFile.file.name} is not supported.`;
                        break;
                    case ErrorCode.TooManyFiles:
                        message = "Maximum number of files exceeded. ";
                        message += filesLimit > 1 ? `You can upload up to ${filesLimit} files.` : "You can upload a single file.";
                        break;
                    default:
                        message = `File ${rejectedFile.file.name} was rejected. `;
                }
                setInternalError(message);
            }
        }
    };
    const validateFileType = (file: File): boolean => {
        if (acceptedFileTypesSet.size === 0) {
            return true;
        }
        const filename = file.name.toLowerCase();
        const lastDotIndex = filename.lastIndexOf(".");
        if (lastDotIndex === -1) {
            return false;
        }
        const extension = filename.slice(lastDotIndex);
        return acceptedFileTypesSet.has(extension);
    };
    const handleRemoveFile = (removedFile: File) => {
        handleChange(files.filter((file) => file !== removedFile));
    };
    const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
        onDrop: handleDrop,
        maxSize: maxFileSizeBytes,
        maxFiles: filesLimit,
        multiple: filesLimit > 1,
        noKeyboard: false,
        noClick: false,
        validator: (file) => {
            if (!validateFileType(file)) {
                return {
                    code: ErrorCode.FileInvalidType,
                    message: "File type not accepted",
                };
            }
            return null;
        },
    });
    const errorToShow = internalError ?? error;
    const acceptString = acceptedFileTypes?.join(",");
    return (<div className={fileUploadStyles.container}>
            <div>
                <InputLabel label={label} htmlFor={inputId} hasRequiredMarker={hasRequiredMarker} hasOptionalMarker={hasOptionalMarker} hasDefaultMarker={hasDefaultMarker} popover={popover}/>
                {description && <FormDescription id={`${inputId}-description`} description={description}/>}
            </div>
            <div {...getRootProps({
        className: cx(fileUploadStyles.uploadArea, {
            [fileUploadStyles.uploadError]: !!errorToShow,
            [fileUploadStyles.dragActive]: isDragActive && !isDragReject,
            [fileUploadStyles.dragReject]: isDragActive && isDragReject,
        }),
    })}>
                <div className={fileUploadStyles.uploadIconContainer}>
                    <CloudArrowUpIcon size={24} color={themeTokens.color.icon.primary}/>
                </div>
                <p className={fileUploadStyles.uploadText}>
                    <span className={fileUploadStyles.boldUploadText}>Browse</span> or drag and drop a file
                </p>
                <input {...getInputProps({
        id: inputId,
        accept: acceptString,
        "aria-describedby": [description ? `${inputId}-description` : null, error ? `${inputId}-error` : null].filter(Boolean).join(" ") || undefined,
        "aria-required": hasRequiredMarker,
    })}/>
            </div>
            {errorToShow && <InputError error={errorToShow} id={`${inputId}-error`}/>}
            {files.length > 0 && (<ul className={fileUploadStyles.fileList} aria-label="File list">
                    {files.map((file, index) => (<li key={index}>
                            <File file={file} onRemove={() => handleRemoveFile(file)}/>
                        </li>))}
                </ul>)}
        </div>);
}
function formatBytes(bytes: number): string {
    if (bytes === 0) {
        return "0 B";
    }
    const baseBytes = 1024;
    const units = ["B", "KB", "MB", "GB", "TB"];
    const targetUnitIndex = Math.floor(Math.log(bytes) / Math.log(baseBytes));
    const unitIndex = Math.min(targetUnitIndex, units.length - 1);
    const divisor = Math.pow(baseBytes, unitIndex);
    const valueInUnit = bytes / divisor;
    const roundedValue = parseFloat(valueInUnit.toFixed(2));
    return `${roundedValue} ${units[unitIndex]}`;
}
interface FileProps {
    file: File;
    onRemove: () => void;
}
function File({ file, onRemove }: FileProps) {
    return (<div className={fileStyles.container}>
            <p className={fileStyles.filename} title={file.name}>
                {file.name}
            </p>
            <Button icon={<TrashIcon size={20}/>} size="small" importance="neutral" onClick={onRemove} accessibleName={`Remove ${file.name}`}/>
        </div>);
}
const uploadIconBackgroundCssVar = "--upload-icon-container-background";
const fileUploadStyles = {
    container: css({
        display: "flex",
        flexDirection: "column",
        gap: space[8],
        width: "100%",
        maxWidth: "600px",
    }),
    uploadArea: css({
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        boxSizing: "border-box",
        padding: `${space[48]} ${space[24]}`,
        gap: space[16],
        height: 180,
        borderRadius: borderRadius.small,
        background: themeTokens.color.background.secondary.default,
        border: `${borderWidth[1]} dashed ${themeTokens.color.border.tertiary}`,
        cursor: "pointer",
        transition: "borderColor 120ms linear",
        "&:hover": {
            borderColor: themeTokens.color.border.bold,
            [uploadIconBackgroundCssVar]: themeTokens.color.background.tertiary,
        },
        "&:focus-visible": {
            borderStyle: "solid",
            outline: `${borderWidth[2]} solid ${themeTokens.color.border.selected}`,
            outlineOffset: "0",
            borderColor: themeTokens.color.border.selected,
        },
    }),
    dragActive: css({
        borderStyle: "solid",
        outline: `${borderWidth[2]} solid ${themeTokens.color.border.selected}`,
        outlineOffset: "0",
        borderColor: themeTokens.color.border.selected,
    }),
    dragReject: css({
        borderStyle: "solid",
        outline: `${borderWidth[2]} solid ${themeTokens.color.border.danger}`,
        outlineOffset: "0",
        borderColor: themeTokens.color.border.danger,
    }),
    uploadError: css({
        borderColor: themeTokens.color.border.danger,
    }),
    uploadIconContainer: css({
        borderRadius: borderRadius.circle,
        background: `var(${uploadIconBackgroundCssVar}, ${themeTokens.color.background.bold})`,
        padding: space[12],
        transition: "background 120ms linear",
    }),
    uploadText: css({
        ...resetStyles.paragraph,
        font: text.body.regular.medium,
        color: themeTokens.color.text.primary,
        textAlign: "center",
    }),
    boldUploadText: css({
        font: text.body.bold.medium,
        color: themeTokens.color.text.link.default,
    }),
    fileList: css({
        ...resetStyles.list,
        display: "flex",
        flexDirection: "column",
        paddingTop: space[4],
        gap: space[6],
    }),
};
const fileStyles = {
    container: css({
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        gap: space[4],
        padding: `${space[4]} ${space[4]} ${space[4]} ${space[12]}`,
        borderRadius: borderRadius.small,
        border: `${borderWidth[1]} solid ${themeTokens.color.border.primary}`,
        background: themeTokens.color.background.secondary.default,
        font: text.body.regular.medium,
    }),
    filename: css({
        ...resetStyles.paragraph,
        flex: 1,
        minWidth: 0,
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "clip",
    }),
};
